// src/components/support/forms/ExtendedDaycare.js
import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useRequestProcessor } from "../../../requestProcessor";
import SpinnerLoading from "../../common/SpinnerLoading";
import FootprintDatePicker from "../../common/FootprintDatePicker";
import moment from "moment";

import { useAtom } from "jotai";
import { childList } from "../../../atomstore/store";
import { useNavigate } from "react-router-dom";

import { submitExtendedDaycare } from "../../../queryfunctions/queryfunctions";
import CustomSelect from "../../common/CustomSelect";

const ExtendedDaycare = () => {
    const navigate = useNavigate();
    const { useMutationWrapper } = useRequestProcessor();
    const [children] = useAtom(childList);

    const durationValues = [
        { key: "Monthly", value: "1 Month" },
        { key: "Weekly", value: "1 Week" },
        { key: "Daily", value: "1 Day" },
    ];

    const [selectedChild, setSelectedChild] = useState(0);
    const [selectedDate, setSelectedDate] = useState("");
    const [serviceDuration, setServiceDuration] = useState("");
    const [holidaySelected, setHolidaySelected] = useState(false);
    const [error, setError] = useState("");

    const extendedDaycare = useMutationWrapper(["extendedDaycare"], () =>
        submitExtendedDaycare(
            selectedDate,
            serviceDuration,
            children[selectedChild]
        )
    );

    const validate = () => {
        if (holidaySelected) {
            setError("The selected date cannot be a holiday");
            return false;
        } else if (!selectedDate) {
            setError("Please select a start date");
            return false;
        } else if (!serviceDuration) {
            setError("Please select program type");
            return false;
        }
        setError("");
        return true;
    };

    const submitRequest = () => {
        if (validate()) extendedDaycare.mutate();
    };

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {extendedDaycare.isLoading && <SpinnerLoading />}

            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Extended Daycare</h2>
            </div>

            <div className="p-4 space-y-4">

                {/* Child Selection */}
                {children.length > 1 && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select Child
                        </label>
                        <CustomSelect
                            value={selectedChild}
                            onChange={(val) => setSelectedChild(Number(val))}
                            options={children.map((child, idx) => ({
                                value: idx,
                                label: `${child?.name} ${child?.last_name}`,
                            }))}
                            placeholder="Select Child"
                        />
                    </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                        Extend daycare services for{" "}
                        {children[selectedChild]?.name.split(" ")[0]}. Choose duration and start date below.
                    </p>
                </div>

                {/* Program Type */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Type
                    </label>
                    <CustomSelect
                        value={serviceDuration}
                        onChange={(val) => {
                            setServiceDuration(val);
                            setError("");
                        }}
                        options={[
                            { value: "", label: "-- Select --" },
                            ...durationValues.map((item, i) => ({
                                value: item.value,
                                label: item.key,
                            })),
                        ]}
                        placeholder="Select Type"
                    />
                </div>

                {/* Start Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                    </label>
                    <FootprintDatePicker
                        holidaySelected={(selected) => {
                            setHolidaySelected(selected);
                            if (selected) setError("The selected date cannot be a holiday");
                        }}
                        minimumDate={moment()}
                        onChange={(date) => {
                            setHolidaySelected(false);
                            setSelectedDate(moment(date));
                            setError("");
                        }}
                        inputProps={{
                            placeholder: "Select Date",
                        }}
                    />
                </div>

                {/* Error */}
                {error && <p className="text-red-500 text-sm">{error}</p>}

                {/* Submit */}
                <button
                    onClick={submitRequest}
                    disabled={extendedDaycare.isLoading}
                    className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition disabled:opacity-60"
                >
                    Submit Request
                </button>
            </div>
        </div>
    );
};

export default ExtendedDaycare;
